import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  IndianRupee, Search, Filter, Check, X, Loader2, RefreshCw, Eye,
  Clock, CreditCard, CheckCircle, Building2, Users, Upload, FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MasterPartnerCommissionManager = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [commissions, setCommissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [masterPartnerFilter, setMasterPartnerFilter] = useState('');
  
  // Modal states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedCommission, setSelectedCommission] = useState(null);
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentScreenshot, setPaymentScreenshot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Screenshot viewer
  const [showScreenshot, setShowScreenshot] = useState(false);
  const [screenshotUrl, setScreenshotUrl] = useState('');

  const getAuthHeader = () => ({ Authorization: `Bearer ${token}` });

  useEffect(() => {
    fetchData();
  }, [statusFilter, masterPartnerFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (masterPartnerFilter) params.append('master_partner_id', masterPartnerFilter);
      
      const [commissionsRes, statsRes] = await Promise.all([
        axios.get(`${API}/master-partners/commissions?${params}`, { headers: getAuthHeader() }),
        axios.get(`${API}/master-partners/commissions/stats`, { headers: getAuthHeader() })
      ]);
      
      setCommissions(commissionsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching commissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (commissionId) => {
    try {
      await axios.post(`${API}/master-partners/commissions/${commissionId}/approve`, {}, { headers: getAuthHeader() });
      fetchData();
    } catch (error) {
      console.error('Error approving commission:', error);
      alert(error.response?.data?.detail || 'Failed to approve commission');
    }
  };

  const openPaymentModal = (commission) => {
    setSelectedCommission(commission);
    setPaymentReference('');
    setPaymentScreenshot(null);
    setShowPaymentModal(true);
  };

  const handleMarkPaid = async () => {
    if (!paymentReference) {
      alert('Please enter a payment reference');
      return;
    }
    
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('payment_reference', paymentReference);
      if (paymentScreenshot) {
        formData.append('payment_screenshot', paymentScreenshot);
      }
      
      await axios.post(
        `${API}/master-partners/commissions/${selectedCommission.id}/mark-paid`,
        formData,
        { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
      );
      
      setShowPaymentModal(false);
      fetchData();
    } catch (error) {
      console.error('Error marking commission as paid:', error);
      alert(error.response?.data?.detail || 'Failed to mark commission as paid');
    } finally {
      setSubmitting(false);
    }
  };

  const viewScreenshot = (path) => {
    setScreenshotUrl(`${BACKEND_URL}${path}`);
    setShowScreenshot(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-700';
      case 'approved': return 'bg-blue-100 text-blue-700';
      case 'pending': return 'bg-amber-100 text-amber-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid': return <CheckCircle className="w-4 h-4" />;
      case 'approved': return <CreditCard className="w-4 h-4" />;
      case 'pending': return <Clock className="w-4 h-4" />;
      default: return null;
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Master Partner Commissions</h1>
          <p className="text-gray-500">Manage commission payouts for Master Partners</p>
        </div>
        <Button onClick={fetchData} variant="outline" className="gap-2">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl p-4 border shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <IndianRupee className="text-gray-500" size={20} />
                <span className="text-sm text-gray-600">Total Commission</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                ₹{(stats.total_commission || 0).toLocaleString()}
              </p>
            </div>
            <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="text-amber-600" size={20} />
                <span className="text-sm text-amber-700">Pending Approval</span>
              </div>
              <p className="text-2xl font-bold text-amber-700">
                ₹{(stats.pending_approval || 0).toLocaleString()}
              </p>
              <p className="text-xs text-amber-600">{stats.pending_approval_count || 0} records</p>
            </div>
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <CreditCard className="text-blue-600" size={20} />
                <span className="text-sm text-blue-700">Approved for Payout</span>
              </div>
              <p className="text-2xl font-bold text-blue-700">
                ₹{(stats.approved_for_payout || 0).toLocaleString()}
              </p>
              <p className="text-xs text-blue-600">{stats.approved_count || 0} records</p>
            </div>
            <div className="bg-green-50 rounded-xl p-4 border border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="text-green-600" size={20} />
                <span className="text-sm text-green-700">Paid Out</span>
              </div>
              <p className="text-2xl font-bold text-green-700">
                ₹{(stats.paid_out || 0).toLocaleString()}
              </p>
              <p className="text-xs text-green-600">{stats.paid_count || 0} records</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <Filter className="w-5 h-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Status</option>
              <option value="pending">Pending Approval</option>
              <option value="approved">Approved for Payout</option>
              <option value="paid">Paid</option>
            </select>
          </div>
        </div>

        {/* Commission Records Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b">
            <h3 className="font-semibold text-gray-900">Commission Records</h3>
          </div>
          
          {loading ? (
            <div className="p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
            </div>
          ) : commissions.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <IndianRupee className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-medium">No Commission Records</h3>
              <p className="mt-1">
                {statusFilter ? 'No commissions match this filter' : 'Commissions will appear here when partners generate revenue'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Master Partner</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">From Partner</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lead/Invoice</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {commissions.map((comm) => (
                    <tr key={comm.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-purple-500" />
                          <span className="font-medium text-gray-900">{comm.master_partner_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-blue-500" />
                          <span className="text-gray-700">{comm.partner_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-gray-900">{comm.lead_name}</p>
                        <p className="text-xs text-gray-500">{comm.invoice_number}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-gray-600">{comm.source_description}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="font-bold text-green-600">₹{comm.commission_amount?.toLocaleString()}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge className={`${getStatusColor(comm.status)} gap-1`}>
                          {getStatusIcon(comm.status)}
                          {comm.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {new Date(comm.created_at).toLocaleDateString('en-IN')}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          {comm.status === 'pending' && (
                            <Button
                              size="sm"
                              onClick={() => handleApprove(comm.id)}
                              className="gap-1"
                            >
                              <Check className="w-3 h-3" />
                              Approve
                            </Button>
                          )}
                          {comm.status === 'approved' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openPaymentModal(comm)}
                              className="gap-1"
                            >
                              <CreditCard className="w-3 h-3" />
                              Mark Paid
                            </Button>
                          )}
                          {comm.status === 'paid' && comm.payment_screenshot && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => viewScreenshot(comm.payment_screenshot)}
                              className="gap-1"
                            >
                              <Eye className="w-3 h-3" />
                              Proof
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Mark Paid Modal */}
        {showPaymentModal && selectedCommission && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
              <div className="p-6 border-b">
                <h3 className="text-lg font-bold text-gray-900">Mark Commission as Paid</h3>
                <p className="text-sm text-gray-500 mt-1">
                  ₹{selectedCommission.commission_amount?.toLocaleString()} to {selectedCommission.master_partner_name}
                </p>
              </div>
              
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Reference *</label>
                  <Input
                    placeholder="Transaction ID / UTR number"
                    value={paymentReference}
                    onChange={(e) => setPaymentReference(e.target.value)}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Screenshot (Optional)</label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                    <input
                      type="file"
                      id="payment-screenshot"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => setPaymentScreenshot(e.target.files[0])}
                    />
                    <label htmlFor="payment-screenshot" className="cursor-pointer">
                      {paymentScreenshot ? (
                        <div className="text-green-600">
                          <FileText className="w-8 h-8 mx-auto mb-2" />
                          <p className="text-sm">{paymentScreenshot.name}</p>
                        </div>
                      ) : (
                        <div className="text-gray-500">
                          <Upload className="w-8 h-8 mx-auto mb-2" />
                          <p className="text-sm">Click to upload payment proof</p>
                        </div>
                      )}
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="p-6 border-t flex justify-end gap-3">
                <Button variant="outline" onClick={() => setShowPaymentModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleMarkPaid} disabled={submitting}>
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Processing...
                    </>
                  ) : (
                    'Confirm Payment'
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Screenshot Viewer Modal */}
        {showScreenshot && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowScreenshot(false)}>
            <div className="bg-white rounded-xl shadow-2xl max-w-3xl max-h-[90vh] overflow-hidden">
              <div className="p-4 border-b flex justify-between items-center">
                <h3 className="font-semibold">Payment Proof</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowScreenshot(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="p-4 overflow-auto">
                <img src={screenshotUrl} alt="Payment Screenshot" className="max-w-full rounded-lg" />
              </div>
            </div>
          </div>
        )}
      </div>
  );
};

export default MasterPartnerCommissionManager;
